export type AdminStore = {
  id: string
  name: string
  address: string | null
  active: boolean
  openingHours?: OpeningHours | null
  fiskalproStoreId: string | null
  fiskalproPosMappings: Array<{ id: string; fiskalproPosId: number; name: string; posNumber: number | null }>
}
export type AdminFiskalShop = { id: number; name: string; city: string | null; pointsOfSale: Array<{ id: number; name: string; posNumber: number | null }> }
export type AdminStoreDraft = { name: string; address: string; fiskalproStoreId: string; fiskalproPosIds: number[] }

const draftOf = (store: AdminStore): AdminStoreDraft => ({
  name: store.name,
  address: store.address ?? '',
  fiskalproStoreId: store.fiskalproStoreId ?? '',
  fiskalproPosIds: (store.fiskalproPosMappings ?? []).map(m => m.fiskalproPosId),
})
const samePos = (a: number[], b: number[]) => [...a].sort((x, y) => x - y).join() === [...b].sort((x, y) => x - y).join()
const GENERIC_NAME = /^prev[aá]dzka\s*\d*$|^predaj[nň]a\s*\d*$/i

import { useDragSort } from './dragSort'
import OpeningHoursEditor, { summarizeHours, type OpeningHours } from './OpeningHoursEditor'
import FiskalProStockSyncAdmin from './FiskalProStockSyncAdmin'

export default function StoresAdmin({ stores, drafts, setDrafts, shops, loading, shopsLoading, savingId, error, onSave, onReload, onLoadShops, onReorder, onHoursSaved }: {
  stores: AdminStore[]
  drafts: Record<string, AdminStoreDraft>
  setDrafts: (update: (current: Record<string, AdminStoreDraft>) => Record<string, AdminStoreDraft>) => void
  shops: AdminFiskalShop[]
  loading: boolean
  shopsLoading: boolean
  savingId: string | null
  error: string
  onSave: (storeId: string) => void | Promise<void>
  onReload: () => void | Promise<void>
  onLoadShops: () => void | Promise<void>
  onReorder: (ids: string[]) => void | Promise<void>
  onHoursSaved: (storeId: string, hours: OpeningHours | null) => void
}) {
  const drag = useDragSort('stores', stores.map(s => s.id), ids => void onReorder(ids), { handle: true })
  const linked = stores.filter(s => s.fiskalproStoreId && (s.fiskalproPosMappings ?? []).length).length
  const withAddress = stores.filter(s => s.address?.trim()).length
  const withHours = stores.filter(s => s.openingHours).length
  const posTotal = shops.reduce((sum, shop) => sum + shop.pointsOfSale.length, 0)

  // Ktorá pokladnica / FiskalPRO prevádzka je už priradená inej predajni (podľa rozpracovaných zmien).
  const draftFor = (store: AdminStore) => drafts[store.id] ?? draftOf(store)
  const shopOwners = (shopId: string, exceptId: string) => stores.filter(s => s.id !== exceptId && draftFor(s).fiskalproStoreId === shopId).map(s => draftFor(s).name)
  const posOwner = (posId: number, exceptId: string) => stores.find(s => s.id !== exceptId && draftFor(s).fiskalproPosIds.includes(posId))

  const setDraft = (store: AdminStore, patch: Partial<AdminStoreDraft>) =>
    setDrafts(current => ({ ...current, [store.id]: { ...(current[store.id] ?? draftOf(store)), ...patch } }))
  const resetDraft = (store: AdminStore) => setDrafts(current => { const next = { ...current }; delete next[store.id]; return next })

  return <>
  <div className="panel stores-admin">
    <div className="pa-head">
      <div>
        <h2>Správa prevádzok</h2>
        <p>Názov a adresu uvidia zákazníci pri výbere osobného odberu – v poradí ako tu (zmeníte potiahnutím za ⋮⋮). Prepojenie s FiskalPRO určuje, do ktorej pokladne idú objednávky.</p>
      </div>
      <div className="pa-head-actions">
        <button type="button" className="pa-ghost" onClick={() => void onLoadShops()} disabled={shopsLoading}>{shopsLoading ? 'Načítavam FiskalPRO…' : '↻ Načítať z FiskalPRO'}</button>
        <button type="button" className="pa-ghost" onClick={() => void onReload()} disabled={loading}>↻ Obnoviť</button>
      </div>
    </div>

    <div className="st-summary">
      <div><strong>{stores.length}</strong><span>prevádzky</span></div>
      <div className={withAddress < stores.length ? 'is-warn' : 'is-ok'}><strong>{withAddress}/{stores.length}</strong><span>s adresou</span></div>
      <div className={withHours < stores.length ? 'is-warn' : 'is-ok'}><strong>{withHours}/{stores.length}</strong><span>s otváracími hodinami</span></div>
      <div className={linked < stores.length ? 'is-warn' : 'is-ok'}><strong>{linked}/{stores.length}</strong><span>prepojené s pokladňou</span></div>
      <div className={shops.length ? 'is-ok' : 'is-warn'}><strong>{shops.length ? `${shops.length} / ${posTotal}` : '—'}</strong><span>{shops.length ? 'prevádzok / pokladníc vo FiskalPRO' : 'zoznam FiskalPRO nenačítaný'}</span></div>
    </div>

    {error && <p className="error-message" role="alert">{error}</p>}

    {loading ? <p className="empty-message">Načítavam prevádzky…</p> : !stores.length ? <p className="empty-message">V databáze sa nenašli prevádzky.</p> :
      <div className="st-grid">{stores.map((store, index) => {
        const draft = draftFor(store)
        const original = draftOf(store)
        const mappingChanged = draft.fiskalproStoreId !== original.fiskalproStoreId || !samePos(draft.fiskalproPosIds, original.fiskalproPosIds)
        const changed = draft.name !== original.name || draft.address !== original.address || mappingChanged
        const shop = shops.find(s => String(s.id) === draft.fiskalproStoreId)
        const savedShop = shops.find(s => String(s.id) === store.fiskalproStoreId)
        const otherOwners = draft.fiskalproStoreId ? shopOwners(draft.fiskalproStoreId, store.id) : []
        const genericName = GENERIC_NAME.test(draft.name.trim())
        const checks = [
          { ok: !genericName && Boolean(draft.name.trim()), label: genericName ? 'Všeobecný názov' : 'Názov' },
          { ok: Boolean(draft.address.trim()), label: draft.address.trim() ? 'Adresa' : 'Chýba adresa' },
          { ok: Boolean(store.openingHours), label: store.openingHours ? 'Otváracie hodiny' : 'Bez otváracích hodín' },
          { ok: Boolean(draft.fiskalproStoreId && draft.fiskalproPosIds.length), label: draft.fiskalproStoreId ? `Pokladnice: ${draft.fiskalproPosIds.length}` : 'Bez pokladne' },
        ]
        const saving = savingId === store.id
        return <article key={store.id} className={`st-card ${changed ? 'is-dirty' : ''} ${store.active ? '' : 'is-inactive'}`} {...drag.itemProps(store.id)}>
          <header className="st-card-head">
            <div className="st-card-title">
              <span {...drag.handleProps(store.id)} className="drag-handle st-handle" title="Potiahnite a zmeňte poradie v e-shope">⋮⋮</span>
              <span className="st-icon" aria-hidden="true">⌂</span>
              <div>
                <strong>{draft.name || 'Bez názvu'}</strong>
                <small>{draft.address || 'Adresa nie je zadaná'}</small>
              </div>
            </div>
            <span className="st-badges">{index === 0 && <span className="st-badge is-first" title="V e-shope je prvá a predvolená pri objednávke">Prvá v e-shope</span>}<span className={`st-badge ${store.active ? 'is-on' : ''}`}>{store.active ? 'Aktívna' : 'Neaktívna'}</span></span>
          </header>

          <div className="st-checks">{checks.map(c => <span key={c.label} className={c.ok ? 'is-ok' : 'is-warn'}>{c.ok ? '✓' : '!'} {c.label}</span>)}</div>

          <section className="st-section">
            <h3>Pre zákazníkov</h3>
            <label className="st-field">Názov prevádzky
              <input value={draft.name} maxLength={120} placeholder="napr. Cukráreň Emili – Kanianka" onChange={e => setDraft(store, { name: e.target.value })} />
              {genericName && <small className="st-hint is-warn">Zákazníci uvidia „{draft.name.trim()}“. Odporúčame názov s mestom, napr. „Cukráreň Emili – Kanianka“.</small>}
            </label>
            <label className="st-field">Adresa
              <input value={draft.address} maxLength={240} placeholder="Ulica a číslo, PSČ Mesto" onChange={e => setDraft(store, { address: e.target.value })} />
            </label>
          </section>

          <section className="st-section">
            <details className="oh-details" open={!store.openingHours || undefined}>
              <summary><h3>Otváracie hodiny</h3><span>{summarizeHours(store.openingHours)}</span></summary>
              <p className="st-hint">Zákazník si v e-shope vyberie termín odberu alebo doručenia iba počas týchto hodín.</p>
              <OpeningHoursEditor storeId={store.id} value={store.openingHours ?? null} onSaved={hours => onHoursSaved(store.id, hours)} />
            </details>
          </section>

          <section className="st-section">
            <h3>Pokladňa FiskalPRO</h3>
            {store.fiskalproStoreId && <p className="st-current">Teraz: <strong>{savedShop ? savedShop.name : `shop ${store.fiskalproStoreId}`}</strong>{store.fiskalproPosMappings?.length ? ` · ${store.fiskalproPosMappings.map(p => p.name).join(', ')}` : ''}</p>}
            {!shops.length ? <div className="st-empty-shops">
              <span>Zoznam prevádzok z FiskalPRO nie je načítaný.</span>
              <button type="button" className="pa-link" disabled={shopsLoading} onClick={() => void onLoadShops()}>Načítať</button>
            </div> : <>
              <label className="st-field">Prevádzka vo FiskalPRO
                <select value={draft.fiskalproStoreId} onChange={e => setDraft(store, { fiskalproStoreId: e.target.value, fiskalproPosIds: [] })}>
                  <option value="">Neprepojená</option>
                  {shops.map(s => <option key={s.id} value={String(s.id)}>{s.name}{s.city && !s.name.toLocaleLowerCase('sk').includes(s.city.toLocaleLowerCase('sk')) ? ` – ${s.city}` : ''}</option>)}
                </select>
                {otherOwners.length > 0 && <small className="st-hint is-warn">Táto FiskalPRO prevádzka je priradená aj k: {otherOwners.join(', ')}.</small>}
              </label>
              {shop && <div className="st-field">
                <span>Pokladnice <small>(objednávky sa posielajú na zaškrtnuté)</small></span>
                <div className="st-pos-list">{shop.pointsOfSale.map(pos => {
                  const on = draft.fiskalproPosIds.includes(pos.id)
                  const owner = posOwner(pos.id, store.id)
                  return <label key={pos.id} className={`st-pos ${on ? 'is-on' : ''}`} title={owner ? `Priradená aj k: ${draftFor(owner).name}` : undefined}>
                    <input type="checkbox" checked={on} onChange={e => setDraft(store, { fiskalproPosIds: e.target.checked ? [...draft.fiskalproPosIds, pos.id] : draft.fiskalproPosIds.filter(id => id !== pos.id) })} />
                    <span><strong>{pos.name}</strong><small>POS {pos.id}{owner ? ` · aj ${draftFor(owner).name}` : ''}</small></span>
                  </label>
                })}</div>
                {mappingChanged && draft.fiskalproStoreId && !draft.fiskalproPosIds.length && <small className="st-hint is-warn">Vyberte aspoň jednu pokladnicu.</small>}
              </div>}
            </>}
          </section>

          <footer className="st-card-foot">
            <code title="Interné ID prevádzky">{store.id}</code>
            <div>
              {changed && <button type="button" className="pa-ghost" disabled={saving} onClick={() => resetDraft(store)}>Zrušiť</button>}
              <button type="button" className="primary-btn" disabled={!changed || saving || !draft.name.trim() || (mappingChanged && Boolean(draft.fiskalproStoreId) && !draft.fiskalproPosIds.length)} onClick={() => void onSave(store.id)}>
                {saving ? 'Ukladám…' : changed ? 'Uložiť zmeny' : 'Uložené'}
              </button>
            </div>
          </footer>
        </article>
      })}</div>}
    <p className="st-note">Pri uložení server znova overí v FiskalPRO, že prevádzka aj pokladnice existujú a patria k sebe. Pri zmene prepojenia sa admin ešte raz opýta.</p>
  </div>
  <FiskalProStockSyncAdmin />
  </>
}
